import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { cabeMaisUmDesafio, quantosAtivos } from "@nhomi/core";
import { nomeDaSessao, useSession } from "@/lib/auth";
import { useMe, usePlano } from "@/lib/plano";
import { ensureUser, watchMyChallenges, type Challenge } from "@/data/challenges";
import { myMembership, type Member } from "@/data/members";
import { dayKey, photoURL } from "@/data/plates";
import { Avatar, Empty, Spinner, TopBar } from "@/ui/primitives";
import { PedirAvisos } from "@/ui/PedirAvisos";
import { IconChevron } from "@/ui/icons";

/**
 * Seus desafios.
 *
 * As duas portas — criar e entrar com código — têm o mesmo peso. No app
 * anterior a home dizia "peça o código pra quem criou o desafio", o que
 * funciona para quem foi convidado e é beco sem saída para quem baixou da
 * loja sem conhecer ninguém.
 */
export function Home() {
  const { user } = useSession();
  const [challenges, setChallenges] = useState<Challenge[] | null>(null);
  const { config, plano, assinante } = usePlano();
  /* O avatar do canto é o de `users/{uid}`: o da sessão é o do login e
     a foto trocada em Conta não o alcança. */
  const me = useMe();

  useEffect(() => {
    if (!user) return;
    // O documento do usuário precisa existir antes de qualquer escrita.
    ensureUser({
      uid: user.uid,
      name: nomeDaSessao(user),
      photo: user.photoURL ?? "",
      email: user.email ?? "",
    }).catch(() => { /* offline: segue com o cache */ });

    return watchMyChallenges(user.uid, setChallenges);
  }, [user]);

  /* A minha ficha em cada desafio, para a lista dizer o que falta fazer.
     Uma leitura por desafio, e a lista é curta por construção — o plano
     limita quantos ficam ativos ao mesmo tempo. Falha silenciosa: sem a
     ficha a linha mostra só o tamanho do grupo, que é o que ela mostrava
     antes de tudo isto. */
  const [minhasFichas, setMinhasFichas] = useState<Record<string, Member | null>>({});
  const idsDosDesafios = (challenges ?? []).map((c) => c.id).join(",");

  useEffect(() => {
    if (!user || !idsDosDesafios) return;
    let vivo = true;
    void Promise.all(
      idsDosDesafios.split(",").map(async (cid) => [cid, await myMembership(cid, user.uid)] as const),
    ).then((pares) => {
      if (vivo) setMinhasFichas(Object.fromEntries(pares));
    });
    return () => { vivo = false; };
  }, [user, idsDosDesafios]);

  /* Ativos, não todos: o limite é de participação simultânea. Um desafio
     que acabou não ocupa vaga, senão o teto seria atingido em dois meses
     e nunca mais liberaria. */
  const hoje = dayKey();
  const ativos = quantosAtivos(challenges ?? [], hoje);
  const cabe = assinante || cabeMaisUmDesafio(config, plano, challenges ?? [], hoje);

  return (
    <main className="screen">
      <TopBar
        action={
          <Link className="topbar-btn" to="/conta" aria-label="Minha conta">
            <Avatar name={me?.name ?? nomeDaSessao(user)} photo={me?.photo} size="sm" />
          </Link>
        }
      />

      <h1 className="page-title">Seus desafios</h1>

      {challenges === null ? (
        <div className="pad"><Spinner /></div>
      ) : challenges.length === 0 ? (
        <>
          <Empty emoji="🍽️" title="Nada por aqui ainda">
            <p>
              Crie um desafio e chame a galera, ou entre num que já existe
              com o código de convite.
            </p>
          </Empty>
          <div className="pad">
            <Link className="btn btn-primary" to="/novo">Criar desafio</Link>
            <Link className="btn btn-outline" to="/entrar-codigo">Entrar com um código</Link>
          </div>
        </>
      ) : (
        <>
          <PedirAvisos temDesafio />

          <div className="pad">
            {challenges.map((c) => (
              <Link key={c.id} className="desafio-card" to={`/c/${c.id}`}>
                <Marca desafio={c} />
                <div className="desafio-corpo">
                  <div className="desafio-nome">{c.name}</div>
                  {/* Uma frase, e só. Tinha um ponto amarelo aqui do lado
                      quando faltava postar: ele não dizia nada que esta
                      linha já não diga, não tinha legenda em lugar nenhum
                      — então não havia como descobrir o que era nem como
                      fazer sair — e ainda substituía a seta, deixando
                      justamente as linhas com pendência com cara de menos
                      tocáveis que as outras. */}
                  <div className="desafio-estado">{estadoDe(c, minhasFichas[c.id], hoje)}</div>
                </div>
                <span className="desafio-chev"><IconChevron size={18} /></span>
              </Link>
            ))}
          </div>
          {/* `acoes-da-lista` e não só `pad`: são dois blocos irmãos dentro
              de `.screen`, que não tem `gap`, então o botão encostava no
              último card — relatado como "colado ao desafio de cima". */}
          <div className="pad acoes-da-lista">
            {cabe ? (
              <>
                <Link className="btn btn-outline" to="/novo">Criar outro desafio</Link>
                <Link className="btn btn-ghost" to="/entrar-codigo">Entrar com um código</Link>
              </>
            ) : (
              /* No teto, os botões saem de cena em vez de ficarem levando
                 a uma tela que vai recusar. A conta aparece porque o
                 limite é de desafios ATIVOS: dizer só "acabou" esconderia
                 que um deles terminando devolve a vaga sozinho. */
              <div className="cota-acabou">
                <div className="cota-titulo">
                  Você está em {ativos} {ativos === 1 ? "desafio" : "desafios"} de uma vez
                </div>
                <div className="cota-sub">
                  O plano grátis vai até {config.desafiosGratis}. Quando um deles
                  terminar a vaga volta sozinha — ou assine pra entrar em quantos
                  quiser.
                </div>
                <Link className="btn-mini" to="/assinar">Ver a assinatura</Link>
              </div>
            )}
          </div>
        </>
      )}
    </main>
  );
}

/** Falta o meu prato de hoje neste desafio? `undefined` = ainda não sei. */
function precisaPostar(c: Challenge, minha: Member | null | undefined, hoje: string): boolean {
  if (!minha || c.endDay && hoje > c.endDay || hoje < c.startDay) return false;
  return !(minha.days ?? []).includes(hoje);
}

/**
 * O que este desafio está pedindo AGORA.
 *
 * Antes a linha dizia "6 participantes · desde 2026-06-01": dois fatos que
 * não mudam nunca e não são motivo pra tocar em nada. Aqui a ordem é a da
 * urgência — o que falta fazer vem antes do que já aconteceu.
 */
function estadoDe(c: Challenge, minha: Member | null | undefined, hoje: string): string {
  const gente = `${c.memberUids.length} ${c.memberUids.length === 1 ? "pessoa" : "pessoas"}`;

  if (hoje < c.startDay) return `Começa em ${dataCurta(c.startDay)} · ${gente}`;
  if (c.endDay && hoje > c.endDay) return `Terminou em ${dataCurta(c.endDay)} · ${gente}`;

  if (c.endDay) {
    const faltam = diasAte(hoje, c.endDay);
    if (faltam <= 3) {
      return faltam === 0
        ? `Último dia · ${gente}`
        : `Termina em ${faltam} ${faltam === 1 ? "dia" : "dias"} · ${gente}`;
    }
  }
  // `undefined` é "a ficha não chegou" — offline, ou regra. Dizer "você
  // ainda não postou" nesse caso seria acusar sem ter lido.
  if (minha === undefined) return gente;
  if (precisaPostar(c, minha, hoje)) return `Você ainda não postou hoje · ${gente}`;
  return `Prato de hoje publicado · ${gente}`;
}

const dataCurta = (dia: string) => {
  const [, m, d] = dia.split("-");
  return m && d ? `${d}/${m}` : dia;
};

/** Dias inteiros entre duas DayKey, ao meio-dia pra não tropeçar no horário de verão. */
function diasAte(de: string, ate: string): number {
  const ms = new Date(`${ate}T12:00:00`).getTime() - new Date(`${de}T12:00:00`).getTime();
  return Math.max(0, Math.round(ms / 86400000));
}

/**
 * A marca do desafio na lista.
 *
 * Sem capa, TODOS os desafios eram o mesmo degradê verde e a lista virava
 * uma pilha de blocos idênticos onde só o nome mudava. A inicial sobre uma
 * cor derivada do `id` dá identidade estável e de graça: o mesmo desafio
 * tem sempre a mesma cor, em qualquer aparelho.
 *
 * Exportada porque a tela de compartilhamento lista os mesmos desafios e
 * precisa da mesma linha: um segundo desenho para a mesma coisa vira duas
 * listas que divergem na primeira mudança.
 */
export function Marca({ desafio }: { desafio: Challenge }) {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    void photoURL(desafio.coverPath ?? null).then((u) => { if (vivo) setUrl(u); });
    return () => { vivo = false; };
  }, [desafio.coverPath]);

  // Soma dos códigos do id: determinística, sem dependência, e espalha bem
  // o suficiente para seis matizes.
  const tom = [...desafio.id].reduce((n, ch) => n + ch.charCodeAt(0), 0) % 6;
  const inicial = desafio.name.trim()[0]?.toUpperCase() ?? "?";

  return (
    <div className={`desafio-marca tom-${tom}`} aria-hidden="true">
      {url ? <img src={url} alt="" /> : inicial}
    </div>
  );
}
